import type { LabwareType, LayoutDetail, ReagentDef, RecipeLabwareDef } from '@ctcells/contract'

/**
 * **새 형식(Opentrons protocol/8) 레시피를 옛 화면 모양으로 읽는다** — 랩웨어 목록(`labware[]`)과 시약 표(`reagents`).
 *
 * 🔴 왜 — v0.9.3 의 새 형식 레시피엔 옛 칸 `payload.labware[]` · `payload.reagents` 가 없다. 소모품 판(`supplySummary` ·
 *    데크 랙 그림 · 시약대 묶음)과 개발용 버튼(칸 수)이 그 칸만 읽어 「시약 배정 정보 없음」이 됐다(eric 2026-10-07
 *    「시약 배정 정보에 왜 아무 것도 안 보여. recipe 에 없는 내용이야?」 — 레시피엔 있다 · `liquids` 13 · `loadLiquid` 13).
 * 🔴 규칙은 서버와 같다(`recipe_v2.labware.context_of` · `labware_type` · `well_order` · `compiler.sample_loads`):
 *    - 랩웨어 = `loadLabware` 명령마다 편집층 `designerApplication.data.labware[id].definitionUri` 로 `labwareDefinitions` 를 찾는다.
 *      종류는 정의 `metadata.tags` 의 `ctcells.type:` 태그 → 없으면 `parameters.isTiprack` → 없으면 `metadata.displayCategory` 가
 *      시약대 부류(`tubeRack` · `aluminumBlock` · `reservoir`)면 `reagent_reservoir`. 칸 이름은 정의 `ordering`(열 차례 · `pick_order: row` 면 줄 차례).
 *    - 고정물 = `designerApplication.data.fixtures[id]`(`type` · `wellOrder` 를 스스로 갖는다 · 예 `assembler.tc_stock`).
 *    - 시약 = 챔버 트레이가 아닌 랩웨어에 싣는 `loadLiquid` 하나가 시약 하나. 채널 = 액체 id(숫자) · 이름 = `liquids[id].displayName` ·
 *      액체 부류 = `data.reagents[id].liquidClass` · 자리 = `labwareId` + `volumeByWell`. 트레이에 싣는 액체(`P1` … `REF`)는 시료다(`trayPathOf`).
 * 🔴 옛 형식은 그대로 돌려준다 — 두 형식을 가르는 곳은 이 파일 하나다.
 */

type Obj = Record<string, unknown>
const asObj = (v: unknown): Obj | undefined =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : undefined

/** 서버 `recipe_v2.labware.TYPE_TAG` */
const TYPE_TAG = 'ctcells.type:'
/** 서버 `recipe_v2.labware._RESERVOIR_CATEGORIES` */
const RESERVOIR_CATEGORIES = new Set(['tubeRack', 'aluminumBlock', 'reservoir'])
/** 서버 `recipe_binding.TRAY_LABWARE_TYPE` */
const TRAY_LABWARE_TYPE = 'hypicc_chamber_tray'

/** 정의의 우리 종류 — 서버 `labware_type` 과 같은 차례. 못 가르면 `undefined`(지어내지 않는다) */
export function definitionType(shape: Obj | undefined): string | undefined {
  const metadata = asObj(shape?.['metadata'])
  const tags = metadata?.['tags']
  for (const tag of Array.isArray(tags) ? (tags as unknown[]) : []) {
    if (typeof tag === 'string' && tag.startsWith(TYPE_TAG))
      return tag.slice(TYPE_TAG.length) || undefined
  }
  if (asObj(shape?.['parameters'])?.['isTiprack'] === true) return 'tip_rack'
  const category = metadata?.['displayCategory']
  return typeof category === 'string' && RESERVOIR_CATEGORIES.has(category)
    ? 'reagent_reservoir'
    : undefined
}

/** 정의 `ordering`(열 차례) 의 칸 이름 — `pick_order: 'row'` 면 줄 차례(서버 `well_order`). `ordering` 이 없으면 `undefined` */
function wellOrder(shape: Obj | undefined, pickOrder: unknown): string[] | undefined {
  const ordering = shape?.['ordering']
  if (!Array.isArray(ordering)) return undefined
  const wells = (ordering as unknown[]).flatMap((column) =>
    Array.isArray(column) ? (column as unknown[]).map(String) : [],
  )
  if (pickOrder !== 'row') return wells
  const key = (well: string): [string, number] => {
    const letters = well.replace(/\d+$/u, '')
    return [letters, Number(well.slice(letters.length)) || 0]
  }
  return wells.toSorted((a, b) => {
    const [la, na] = key(a)
    const [lb, nb] = key(b)
    return la === lb ? na - nb : la < lb ? -1 : 1
  })
}

function isV2(doc: Obj | undefined): boolean {
  return Array.isArray(doc?.['commands']) && asObj(doc?.['designerApplication']) !== undefined
}

/**
 * 레시피 랩웨어 목록(옛 모양 `RecipeLabwareDef[]`) — 옛 형식은 `payload.labware` 그대로, 새 형식은 `loadLabware` + 고정물에서.
 * 종류를 못 가르는 랩웨어는 `type` 없이 두지 않고 **뺀다**(칸 수 · 시약대 묶음이 종류로 고른다 — 모르는 것을 섞지 않는다).
 */
export function labwareOf(payload: unknown): RecipeLabwareDef[] | undefined {
  const doc = asObj(payload)
  const legacy = doc?.['labware']
  if (Array.isArray(legacy)) return legacy as RecipeLabwareDef[]
  if (!isV2(doc)) return undefined
  const data = asObj(asObj(doc?.['designerApplication'])?.['data'])
  const ours = asObj(data?.['labware']) ?? {}
  const definitions = asObj(doc?.['labwareDefinitions']) ?? {}
  const out: RecipeLabwareDef[] = []
  for (const cmd of doc?.['commands'] as unknown[]) {
    const command = asObj(cmd)
    if (command?.['commandType'] !== 'loadLabware') continue
    const id = asObj(command['params'])?.['labwareId']
    if (typeof id !== 'string') continue
    const mine = asObj(ours[id])
    const uri = mine?.['definitionUri']
    const shape = typeof uri === 'string' ? asObj(definitions[uri]) : undefined
    const type = definitionType(shape)
    if (type === undefined) continue
    const wells = wellOrder(shape, mine?.['pick_order'])
    const displayName = asObj(shape?.['metadata'])?.['displayName']
    const layout = mine?.['layout_detail']
    out.push({
      id,
      /* 🔴 종류 어휘의 권위는 서버 `labware_type`(정의 태그 · isTiprack · displayCategory)이다 — 옛 enum 에 없는 값도 그대로 싣는다 */
      type: type as LabwareType,
      layout_detail: (typeof layout === 'string' ? layout : 'simple') as LayoutDetail,
      ...(typeof displayName === 'string' ? { display_name: displayName } : {}),
      ...(wells === undefined ? {} : { well_names: wells }),
    })
  }
  for (const [id, fixture] of Object.entries(asObj(data?.['fixtures']) ?? {})) {
    const entry = asObj(fixture)
    const type = entry?.['type']
    if (typeof type !== 'string') continue
    const order = entry?.['wellOrder']
    const displayName = entry?.['displayName']
    out.push({
      id,
      type: type as LabwareType,
      layout_detail: 'simple',
      ...(typeof displayName === 'string' ? { display_name: displayName } : {}),
      ...(Array.isArray(order) ? { well_names: (order as unknown[]).map(String) } : {}),
    })
  }
  return out
}

/**
 * 레시피 시약 표(옛 모양 `Record<채널, ReagentDef>`) — 옛 형식은 `payload.reagents` 그대로, 새 형식은 시약대에 싣는 `loadLiquid` 에서.
 * 같은 액체를 여러 번 실으면(칸이 여럿) `volumeByWell` 을 합친다. 채널은 액체 id 가 숫자면 그 수, 아니면 적힌 차례다.
 */
export function reagentsOf(payload: unknown): Record<string, ReagentDef> | undefined {
  const doc = asObj(payload)
  const legacy = asObj(doc?.['reagents'])
  if (legacy !== undefined) return legacy as Record<string, ReagentDef>
  if (!isV2(doc)) return undefined
  const trays = new Set(
    (labwareOf(payload) ?? [])
      /* 옛 enum 에 없는 서버 종류 이름이라 문자열로 맞댄다 */
      .filter((lw) => (lw.type as string) === TRAY_LABWARE_TYPE)
      .map((lw) => lw.id),
  )
  const data = asObj(asObj(doc?.['designerApplication'])?.['data'])
  const liquids = asObj(doc?.['liquids']) ?? {}
  const classes = asObj(data?.['reagents']) ?? {}
  const out: Record<string, ReagentDef> = {}
  let index = 0
  for (const cmd of doc?.['commands'] as unknown[]) {
    const command = asObj(cmd)
    if (command?.['commandType'] !== 'loadLiquid') continue
    const params = asObj(command['params'])
    const liquidId = params?.['liquidId']
    const labwareId = params?.['labwareId']
    const byWell = asObj(params?.['volumeByWell'])
    if (typeof liquidId !== 'string' || typeof labwareId !== 'string' || byWell === undefined)
      continue
    if (trays.has(labwareId)) continue // 트레이에 싣는 액체는 시료(`P1` … `REF`)다
    index += 1
    const held = out[liquidId]
    const volumes = Object.fromEntries(
      Object.entries(byWell).filter(
        (entry): entry is [string, number] => typeof entry[1] === 'number',
      ),
    )
    if (held !== undefined) {
      held.volumeByWell = { ...(held.volumeByWell ?? {}), ...volumes }
      continue
    }
    const liquid = asObj(liquids[liquidId])
    const displayName = liquid?.['displayName']
    /* 성분명 — 옛 형식 `reagents[].name` 자리(화면 이름은 `display_name`). 새 형식은 액체 `description`(「16% 포름알데히드」) · 없으면 화면 이름 */
    const description = liquid?.['description']
    const liquidClass = asObj(classes[liquidId])?.['liquidClass']
    const numeric = Number(liquidId)
    out[liquidId] = {
      channel: Number.isInteger(numeric) && numeric > 0 ? numeric : index,
      name:
        typeof description === 'string' && description.trim() !== ''
          ? description.trim()
          : typeof displayName === 'string'
            ? displayName
            : liquidId,
      ...(typeof displayName === 'string' ? { display_name: displayName } : {}),
      liquid_class: typeof liquidClass === 'string' ? liquidClass : '',
      labware_id: labwareId,
      volumeByWell: volumes,
    }
  }
  return out
}
